import { useEffect, useState } from 'react';
import { apiClient } from '@kss/api';
import { Card, SearchableSelect, useToast } from '@kss/ui';
import { Printer, Save, Upload, X } from 'lucide-react';
import {
  FORMAT_CATALOG, MODULE_FORMATS, PRINT_MODULES,
  getAssignedFormat, setAssignedFormat, resetFormats,
  type PrintModule, type PrintFormatId,
} from '../../print/formats';
import { getPrintBranding, setPrintBranding, type PrintBranding } from '../../print/branding';
import { INVOICE_COLUMNS, getInvoiceColumns, setInvoiceColumns, type InvoiceColumnConfig } from '../../print/invoiceColumns';
import { AuthImg } from '../../components/AuthImg';

/**
 * Settings → Print Formats. Admin assigns one print template per
 * printable module (Sales Invoice, Purchase Invoice, POS, Restaurant
 * bill, KOT). Each print path honors the choice via getAssignedFormat().
 *
 * Stored in a tenant-scoped localStorage envelope (see print/formats.ts);
 * backend persistence is filed as a gap so it can become device-shared.
 */
export default function PrintFormats() {
  const toast = useToast();
  const [assign, setAssign] = useState<Record<PrintModule, PrintFormatId>>(() => {
    const o = {} as Record<PrintModule, PrintFormatId>;
    for (const m of PRINT_MODULES) o[m] = getAssignedFormat(m);
    return o;
  });

  function choose(m: PrintModule, f: PrintFormatId) {
    setAssignedFormat(m, f);
    setAssign((prev) => ({ ...prev, [m]: f }));
    toast.success('Saved', `${MODULE_FORMATS[m].label} → ${FORMAT_CATALOG[f].label}`);
  }

  function reset() {
    resetFormats();
    const o = {} as Record<PrintModule, PrintFormatId>;
    for (const m of PRINT_MODULES) o[m] = getAssignedFormat(m);
    setAssign(o);
    toast.success('Reset', 'All modules back to their default format.');
  }

  // ── Print branding (signature image, QR, bank details) ──────────
  const [branding, setBranding] = useState<PrintBranding>(() => getPrintBranding());
  function updateBranding(patch: Partial<PrintBranding>) {
    setBranding((prev) => {
      const next = { ...prev, ...patch };
      setPrintBranding(next);   // local draft — persisted to the DB via "Save to server"
      return next;
    });
  }

  // Load branding from the tenant row (backend) on mount so it's shared across
  // devices/users; a local unsaved draft still wins so in-progress edits aren't lost.
  const [tenantRowId, setTenantRowId] = useState<string | number | null>(null);
  const [savingBrand, setSavingBrand] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const raw = (await apiClient.get('/tenants/Tenant/me')).data as Record<string, unknown>;
        const d = ((raw?.data as Record<string, unknown>) ?? raw) ?? {};
        if (cancelled) return;
        setTenantRowId((d.id ?? d.tenantId ?? null) as string | number | null);
        const s = (k: string) => { const v = d[k]; return v == null || v === '' ? undefined : String(v); };
        const backend: PrintBranding = {
          logoUrl: s('logoUrl'), signatureUrl: s('signatureImageUrl'), qrUrl: s('qrImageUrl'),
          bankName: s('bankName'), bankAccountName: s('bankAccountName'), bankAccountNo: s('bankAccountNo'),
          bankIfsc: s('bankIfsc'), bankBranch: s('bankBranch'),
          tenantName: s('name'), tenantGstin: s('gstin'), tenantPhone: s('phone'), tenantEmail: s('email'),
          tenantAddress: s('address'), tenantCity: s('city'), tenantState: s('state'), tenantPincode: s('pincode'),
        };
        const clean = (o: PrintBranding) => Object.fromEntries(Object.entries(o).filter(([, v]) => v != null && v !== '')) as PrintBranding;
        const merged = { ...clean(backend), ...clean(getPrintBranding()) };
        setBranding(merged);
        setPrintBranding(merged);
      } catch { /* backend unreachable — keep the localStorage draft */ }
    })();
    return () => { cancelled = true; };
  }, []);

  async function saveBrandingToBackend() {
    setSavingBrand(true);
    try {
      // Backend PUT is a FULL replace — fetch the whole tenant row, spread it,
      // then override only the branding columns so nothing else is nulled.
      let full: Record<string, unknown> = {};
      if (tenantRowId != null) {
        try {
          const r = (await apiClient.get(`/tenancy/Tenant/${encodeURIComponent(String(tenantRowId))}`)).data as Record<string, unknown>;
          full = ((r?.data as Record<string, unknown>) ?? r) ?? {};
        } catch { /* fall through with an empty base */ }
      }
      const payload: Record<string, unknown> = {
        ...full,
        logoUrl: branding.logoUrl ?? '',
        signatureImageUrl: branding.signatureUrl ?? '',
        qrImageUrl: branding.qrUrl ?? '',
        bankName: branding.bankName ?? '',
        bankAccountName: branding.bankAccountName ?? '',
        bankAccountNo: branding.bankAccountNo ?? '',
        bankIfsc: branding.bankIfsc ?? '',
        bankBranch: branding.bankBranch ?? '',
        ...(branding.tenantName ? { name: branding.tenantName, legalName: branding.tenantName } : {}),
        ...(branding.tenantGstin ? { gstin: branding.tenantGstin } : {}),
        ...(branding.tenantPhone ? { contactPhone: branding.tenantPhone } : {}),
        ...(branding.tenantEmail ? { contactEmail: branding.tenantEmail } : {}),
        ...(branding.tenantAddress ? { address: branding.tenantAddress } : {}),
        ...(branding.tenantCity ? { city: branding.tenantCity } : {}),
        ...(branding.tenantState ? { state: branding.tenantState } : {}),
        ...(branding.tenantPincode ? { pincode: branding.tenantPincode } : {}),
      };
      await apiClient.put('/tenancy/Tenant', payload);
      setPrintBranding(branding);   // keep the local mirror in sync for immediate prints
      toast.success('Saved to server', 'Print branding is now stored in the database — shared across all users & devices.');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Could not save to the server.');
    } finally {
      setSavingBrand(false);
    }
  }
  function onPickImage(field: 'signatureUrl' | 'qrUrl' | 'logoUrl', file: File | null) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { persistImage } = await import('../../utils/persistImage');
        const url = await persistImage(String(reader.result));
        updateBranding({ [field]: url });
        toast.success('Uploaded', field === 'signatureUrl' ? 'Signature image saved' : field === 'qrUrl' ? 'QR image saved' : 'Logo saved');
      } catch {
        toast.error('Upload failed', 'Could not host the image.');
      }
    };
    reader.readAsDataURL(file);
  }
  // ── Invoice line-item columns (admin chooses which optional cols print) ──
  const [invCols, setInvCols] = useState<InvoiceColumnConfig>(() => getInvoiceColumns());
  function toggleCol(key: keyof InvoiceColumnConfig, on: boolean) {
    setInvCols((prev) => {
      const next = { ...prev, [key]: on };
      setInvoiceColumns(next);
      return next;
    });
    toast.success('Saved', `Column ${on ? 'shown' : 'hidden'} on invoices`);
  }

  const bankFields: Array<{ key: keyof PrintBranding; label: string }> = [
    { key: 'bankName', label: 'Bank Name' },
    { key: 'bankAccountName', label: 'Account Name' },
    { key: 'bankAccountNo', label: 'Account No.' },
    { key: 'bankIfsc', label: 'IFSC Code' },
    { key: 'bankBranch', label: 'Branch' },
  ];
  const tenantFields: Array<{ key: keyof PrintBranding; label: string }> = [
    { key: 'tenantName', label: 'Company / Tenant Name' },
    { key: 'tenantGstin', label: 'GSTIN' },
    { key: 'tenantPhone', label: 'Phone' },
    { key: 'tenantEmail', label: 'Email' },
    { key: 'tenantAddress', label: 'Address' },
    { key: 'tenantCity', label: 'City' },
    { key: 'tenantState', label: 'State' },
    { key: 'tenantPincode', label: 'Pincode' },
  ];

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
          <Printer className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Print Formats</h1>
          <p className="text-xs text-content-muted">
            Assign an invoice/receipt format to each module. Printing uses the assigned format.
          </p>
        </div>
        <button
          type="button"
          onClick={reset}
          className="rounded-md border border-border px-3 py-1.5 text-xs text-content-secondary hover:border-primary"
        >
          Reset to defaults
        </button>
      </div>

      <Card className="p-0">
        <div className="divide-y divide-border">
          {PRINT_MODULES.map((m) => {
            const cfg = MODULE_FORMATS[m];
            return (
              <div key={m} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-[180px] flex-1">
                  <div className="text-sm font-medium text-content-primary">{cfg.label}</div>
                  <div className="text-[11px] text-content-muted">
                    Default: {FORMAT_CATALOG[cfg.default].label}
                  </div>
                </div>
                <div className="w-64">
                  {cfg.options.length > 1 ? (
                    <SearchableSelect
                      value={assign[m]}
                      onChange={(v) => v && choose(m, v as PrintFormatId)}
                      options={cfg.options.map((id) => ({
                        value: id,
                        label: `${FORMAT_CATALOG[id].label} · ${FORMAT_CATALOG[id].paper}`,
                      }))}
                      placeholder="Select format…"
                    />
                  ) : (
                    <span className="inline-flex rounded-md border border-border bg-surface-app px-3 py-1.5 text-xs text-content-secondary">
                      {FORMAT_CATALOG[assign[m]].label} · {FORMAT_CATALOG[assign[m]].paper}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Invoice columns — admin picks which OPTIONAL line columns print. */}
      <Card className="p-4">
        <h2 className="mb-1 text-sm font-semibold text-content-primary">Invoice Columns</h2>
        <p className="mb-3 text-[11px] text-content-muted">
          Choose which optional line-item columns appear on the printed invoice / order / note.
          Sr, Description, Qty, Rate &amp; Taxable always show. Applies to all invoice types.
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {INVOICE_COLUMNS.map((c) => (
            <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-surface-card px-3 py-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={invCols[c.key]}
                onChange={(e) => toggleCol(c.key, e.target.checked)}
              />
              <span className="text-content-primary">{c.label}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* Print branding — uploadable signature + QR + bank details. */}
      <Card className="p-4">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-content-primary">Print Branding</h2>
          <button
            type="button"
            onClick={() => void saveBrandingToBackend()}
            disabled={savingBrand}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-60"
          >
            <Save className="h-3.5 w-3.5" /> {savingBrand ? 'Saving…' : 'Save to server'}
          </button>
        </div>
        <p className="mb-3 text-[11px] text-content-muted">
          Uploaded here and shown on the invoice / order print (Company Logo, QR, Authorised
          Signatory, bank block). Click <b>Save to server</b> to store it in the database so it&rsquo;s
          shared across all users &amp; devices.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {(['logoUrl', 'signatureUrl', 'qrUrl'] as const).map((field) => (
            <div key={field} className="flex flex-col gap-1">
              <span className="text-[11px] font-medium text-content-secondary">
                {field === 'signatureUrl' ? 'Authorised Signatory' : field === 'qrUrl' ? 'QR Code' : 'Company Logo'}
              </span>
              <div className="flex items-center gap-3">
                <div className="grid h-20 w-32 place-items-center overflow-hidden rounded border border-border bg-surface-app">
                  {branding[field]
                    ? <AuthImg src={String(branding[field])} className="max-h-20 max-w-full object-contain" />
                    : <span className="text-[10px] text-content-muted">No image</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-primary hover:bg-surface-card-hover">
                    <Upload className="h-3.5 w-3.5" /> Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage(field, e.target.files?.[0] ?? null)} />
                  </label>
                  {branding[field] && (
                    <button type="button" onClick={() => updateBranding({ [field]: undefined })}
                      className="inline-flex items-center gap-1 text-[11px] text-danger hover:underline">
                      <X className="h-3 w-3" /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {bankFields.map(({ key, label }) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-[11px] font-medium text-content-secondary">{label}</span>
              <input
                className="h-9 rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary"
                value={String(branding[key] ?? '')}
                onChange={(e) => updateBranding({ [key]: e.target.value })}
              />
            </label>
          ))}
        </div>
      </Card>

      {/* Company / tenant header for prints — no backend endpoint (G86). */}
      <Card className="space-y-3 p-4">
        <div>
          <h2 className="text-sm font-semibold text-content-primary">Company Header (prints)</h2>
          <p className="text-[11px] text-content-muted">
            Shown as the seller/company header on every invoice &amp; order print. The backend exposes no
            tenant-info endpoint (gap G86), so set your details here.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {tenantFields.map(({ key, label }) => (
            <label key={key} className="flex flex-col gap-1">
              <span className="text-[11px] font-medium text-content-secondary">{label}</span>
              <input
                className="h-9 rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary"
                value={String(branding[key] ?? '')}
                onChange={(e) => updateBranding({ [key]: e.target.value })}
              />
            </label>
          ))}
        </div>
      </Card>

      <p className="px-1 text-[11px] text-content-muted">
        Edits are drafted on this device as you type. Click <b>Save to server</b> (Print Branding card)
        to store the logo / signature / QR / bank / company header in the database — then it&rsquo;s the
        same for every user on every device. Uploaded images are held in the backend image store
        (local disk by default; S3 / Cloudflare R2 / Bunny configurable server-side).
      </p>
    </div>
  );
}
